import React, { useEffect } from 'react';
import { Box, Typography, Grid, Select, MenuItem, FormControl, InputLabel, Paper } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { setSelectedTerms, updateCalculations } from '../../store/presentationSlice';
import { calculateProposedPayment, calculateTotalSavings } from '../../utils/calculations';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import SlideHeader from './SlideHeader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const mockCreditReport = {
  totalBalance: 42000,
  totalMonthlyPayment: 1260
};

const TermOptionsSlide: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedTerms, calculations } = useSelector((state: RootState) => state.presentation);

  useEffect(() => {
    const proposedMonthlyPayment = calculateProposedPayment(
      mockCreditReport.totalBalance,
      selectedTerms
    );

    const totalSavings = calculateTotalSavings(
      mockCreditReport.totalBalance,
      proposedMonthlyPayment,
      selectedTerms,
      mockCreditReport.totalMonthlyPayment
    );

    dispatch(updateCalculations({
      proposedMonthlyPayment,
      currentMonthlyPayment: mockCreditReport.totalMonthlyPayment,
      totalSavings
    }));
  }, [selectedTerms, dispatch]);

  const handleTermChange = (event: any) => {
    dispatch(setSelectedTerms(event.target.value));
  };

  const chartData = {
    labels: ['Current', 'Proposed'],
    datasets: [
      {
        label: 'Monthly Payment',
        data: [calculations.currentMonthlyPayment, calculations.proposedMonthlyPayment],
        borderColor: '#0A1929',
        backgroundColor: '#0A192980',
      }
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Payment Comparison',
      },
    },
  };

  const monthlySavings = calculations.currentMonthlyPayment - calculations.proposedMonthlyPayment;
  const annualSavings = monthlySavings * 12;

  // Define common styles
  const commonPaperStyle = {
    p: 2.5,
    borderRadius: 1,
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    border: '1px solid #DEE2E6',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  };

  const sectionTitleStyle = {
    color: '#0A1929',
    fontWeight: 600,
    fontSize: '1.4rem',
    mb: 2.5,
    display: 'flex',
    alignItems: 'center',
    '&::before': {
      content: '""',
      width: '4px',
      height: '24px',
      bgcolor: '#0A1929',
      mr: 2,
      borderRadius: '2px',
      display: 'inline-block'
    }
  };

  return (
    <Box>
      <SlideHeader title="Term Options" />
      
      {/* Content Section */}
      <Box sx={{ px: 3, pb: 3 }}>
        {/* Current Terms Section */}
        <Box sx={{ 
          mb: 4,
          border: '1px solid #DEE2E6',
          borderRadius: 1,
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography variant="h6" sx={{ 
            color: '#0A1929',
            fontWeight: 600,
            fontSize: { xs: '1.1rem', sm: '1.2rem' },
            mr: { xs: 0, sm: 3 },
            minWidth: { xs: 'auto', sm: '120px' },
            width: { xs: '100%', sm: 'auto' },
            textAlign: { xs: 'center', sm: 'left' },
            '&::before': {
              content: '""',
              width: '3px',
              height: '18px',
              bgcolor: '#0A1929',
              mr: 1.5,
              borderRadius: '2px',
              display: 'inline-block',
              verticalAlign: 'middle'
            }
          }}>
            Current Terms
          </Typography>
          <Box sx={{ flexGrow: 1, width: '100%' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  ...commonPaperStyle,
                  bgcolor: '#F8F9FA',
                  py: { xs: 2, sm: 1.5 },
                  px: { xs: 2.5, sm: 2 },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 0.5,
                    color: '#495057',
                    fontWeight: 500
                  }}>
                    Current Projected Payoff Time
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    color: '#0A1929',
                    fontWeight: 500,
                    fontSize: { xs: '1.25rem', sm: '1.25rem' }
                  }}>
                    5 Years 9 Months
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  ...commonPaperStyle,
                  bgcolor: '#F8F9FA',
                  py: { xs: 2, sm: 1.5 },
                  px: { xs: 2.5, sm: 2 },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 0.5,
                    color: '#495057',
                    fontWeight: 500
                  }}>
                    Current Monthly Payment
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    color: '#0A1929',
                    fontWeight: 500,
                    fontSize: { xs: '1.25rem', sm: '1.25rem' }
                  }}>
                    ${calculations.currentMonthlyPayment.toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* New Payment Options and Savings Section */}
        <Box sx={{ 
          border: '1px solid #DEE2E6',
          borderRadius: 1,
          p: 3
        }}>
          <Typography variant="h4" sx={{ 
            ...sectionTitleStyle, 
            mb: 3,
            fontSize: '2rem',
            fontWeight: 600,
            color: '#0A1929',
            '&::before': {
              ...sectionTitleStyle['&::before'],
              bgcolor: '#0A1929'
            }
          }}>
            Find Your New Monthly Payment
          </Typography>
          <Paper sx={{
            ...commonPaperStyle,
            p: 3,
            border: '1px solid #DEE2E6',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            }
          }}>
            {/* Term Selection */}
            <Box sx={{ 
              mb: 3,
              pb: 3,
              borderBottom: '1px solid #DEE2E6'
            }}>
              <Typography variant="subtitle1" sx={{ 
                mb: 2,
                color: '#495057',
                fontWeight: 600,
                fontSize: '1.1rem'
              }}>
                Select New Terms
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedTerms}
                  onChange={handleTermChange}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#DEE2E6'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0A1929'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0A1929'
                    },
                    height: '50px'
                  }}
                >
                  {[12, 24, 36, 48, 53, 56].map((term) => (
                    <MenuItem 
                      key={term} 
                      value={term}
                      sx={{
                        '&:hover': {
                          bgcolor: '#f8f9fa'
                        },
                        '&.Mui-selected': {
                          bgcolor: '#e8f4ff',
                          '&:hover': {
                            bgcolor: '#e8f4ff'
                          }
                        }
                      }}
                    >
                      {term} Months
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Results Section */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: '#e8f4ff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#495057', mb: 1 }}>
                    New Monthly Payment
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    color: '#0A1929',
                    fontWeight: 600,
                    mb: 1
                  }}>
                    ${Math.round(calculations.proposedMonthlyPayment).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#2E7D32',
                    fontWeight: 500
                  }}>
                    Save ${Math.round(monthlySavings).toLocaleString()}/month
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: '#fff8e1',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#495057', mb: 1 }}>
                    Total Program Cost
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    color: '#0A1929',
                    fontWeight: 600,
                    mb: 1
                  }}>
                    ${Math.round(calculations.proposedMonthlyPayment * selectedTerms).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#ED6C02',
                    fontWeight: 500
                  }}>
                    Over {selectedTerms} Months
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: '#f1f8f1',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#495057', mb: 1 }}>
                    Total Program Savings
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    color: '#0A1929',
                    fontWeight: 600,
                    mb: 1
                  }}>
                    ${Math.round(calculations.totalSavings).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#2E7D32',
                    fontWeight: 500
                  }}>
                    Over {selectedTerms} Months
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default TermOptionsSlide;
